import { format, isValid, parseISO } from 'date-fns';
import { DateFormatType } from '../types';
import { LocalizationPipe } from '@shared/modules/localization';

export class DateUtils {
  static getYearsTillCurrentYear(startYear: number): number[] {
    const currentYear = new Date().getFullYear();

    // Using Array.from to create an array of years
    const yearsArray = Array.from(
      { length: currentYear - startYear + 1 },
      (_, index) => startYear + index,
    );

    return yearsArray.reverse();
  }

  static getCurrentYear(): number {
    return new Date().getFullYear();
  }

  static now(): string {
    return new Date().toISOString();
  }

  static startDate(date?: Date | string): string {
    const currentDate = date ? (date instanceof Date ? date : new Date(date)) : new Date();
    currentDate.setHours(0, 0, 0, 0);
    return currentDate.toISOString();
  }
  static endDate(date?: Date | string): string {
    const currentDate = date ? (date instanceof Date ? date : new Date(date)) : new Date();
    currentDate.setHours(23, 59, 59, 999);
    return currentDate.toISOString();
  }

  static convertToDateTime(timeString: string): Date {
    // Create a new Date object for the current date
    const currentDate = new Date();

    // Split the timeString into its components
    const timeParts = timeString.split(':').map(Number);

    // Check how many parts we have (to determine if seconds are included)
    const hours = timeParts[0];
    const minutes = timeParts[1];
    let seconds = 0; // Default to 0 seconds if not specified

    // If there are seconds in the timeParts, update seconds variable
    if (timeParts.length === 3) {
      seconds = timeParts[2];
    }

    // Set hours, minutes, and seconds to the currentDate
    currentDate.setHours(hours, minutes, seconds);

    return currentDate;
  }

  static convertDateStringToTimestamp(dateString: string): number {
    const [datePart, timePart] = dateString.trim().split(' ');
    const [day, month, year] = datePart.split('.').map(Number);

    let hours = 0;
    let minutes = 0;

    if (timePart) {
      const [h, m] = timePart.split(':').map(Number);
      hours = h || 0;
      minutes = m || 0;
    }

    const date = new Date(year, month - 1, day, hours, minutes);
    return date.getTime();
  }

  static transform(value: any, args: DateFormatType = 'dd.MM.yyyy'): string {
    if (!value) return '--.--.--';

    // Handle Firestore timestamp objects
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
      // TODO: remove after migration from Firestore
      value = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
    }

    // Проверяем тип значения
    if (typeof value === 'string') {
      // Если строка содержит только цифры - это миллисекунды
      if (/^\d+$/.test(value)) {
        value = new Date(parseInt(value));
      } else {
        if (typeof value === 'string' && value.match(/^\d{2}:\d{2}:\d{2}$/)) {
          if (args === 'HH:mm') {
            value = value.substring(0, 5);
          }
        } else {
          // Иначе это ISO строка
          value = new Date(value);
        }
      }
    }

    if (typeof value === 'number') {
      // Если число - это миллисекунды
      value = new Date(value);
    }

    if (isValid(value)) {
      return format(value, args);
    }

    return '--.--.--';
  }

  static isValidTimeFormat(timeString: string): boolean {
    const regex = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;
    return regex.test(timeString);
  }

  static isValidDate(dateString?: string): boolean {
    if (!dateString) {
      return false;
    }
    const parsedDate = parseISO(dateString);
    return isValid(parsedDate);
  }

  static generateYearList(currentYear: number): number[] {
    const years = [];
    for (let i = 0; i < 6; i++) {
      years.push(currentYear + 1 - i);
    }
    return years;
  }

  static durationFormat(
    value: { years?: number; months?: number; days?: number },
    emptyText = '',
  ): string {
    const pluralize = (n: number, one: string, few: string, many: string) => {
      const mod10 = n % 10;
      const mod100 = n % 100;

      if (mod10 === 1 && mod100 !== 11) return one;
      if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return few;
      return many;
    };

    if (!value || (value.years == null && value.months == null && value.days == null)) {
      return '';
    }

    const parts: string[] = [];

    if (value.years) {
      parts.push(`${value.years} ${pluralize(value.years, 'год', 'года', 'лет')}`);
    }

    if (value.months) {
      parts.push(`${value.months} ${pluralize(value.months, 'месяц', 'месяца', 'месяцев')}`);
    }

    if (value.days) {
      parts.push(`${value.days} ${pluralize(value.days, 'день', 'дня', 'дней')}`);
    }

    return parts.length === 0 ? emptyText : parts.join(' ');
  }

  static convertDateToTimeMs(date: number) {
    return Math.floor(new Date(date).getTime());
  }

  static formatDateToLocalString(
    value: Date | string | number | null | undefined,
    locale = 'ru-RU',
  ): string {
    // TODO: get locale from selected lang in Localization serivce or lastSelectedLang in localStorage
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);

    if (isNaN(date.getTime())) {
      return '';
    }
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString(locale, options);
  }

  static formatHoursWord(range: string, localization: LocalizationPipe): string {
    // Для случая "6+"
    if (range === '6+') {
      return `${ localization.transform('HOURS_MORE_THAN_6') }`;
    }

    // Извлекаем первое число из диапазона (например, "0-1" -> 0, "1-2" -> 1)
    const firstNum = parseInt(range.split('-')[0]);

    // Правила склонения для русского языка
    const lastDigit = firstNum % 10;
    const lastTwoDigits = firstNum % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
      return `${range} ${ localization.transform('HOURS_MANY') }`;
    }

    if (lastDigit === 1) {
      return `${range} ${ localization.transform('HOURS_ONE') }`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
      return `${range} ${ localization.transform('HOURS_FEW') }`;
    }

    return `${range} ${ localization.transform('HOURS_MANY') }`;
  }
}
